import { useState, type CSSProperties } from 'react'
import type { UserRelation } from '../../types/api'
import type { TrainingProgramActivation } from '../../types/api'
import { Avatar, Button } from '../ui'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import type { ApiError } from '../../api/client'

const WEEK_PRESETS = [1, 2, 4, 6, 8] as const

function nearestPresetWeeks(weeks: number): number {
  return WEEK_PRESETS.reduce((best, w) =>
    Math.abs(w - weeks) < Math.abs(best - weeks) ? w : best
  )
}

interface Props {
  programId: number
  programName: string
  trainees: UserRelation[]
  assignedTraineeIds: number[]
  onClose: () => void
  onAssigned: (activation: TrainingProgramActivation, traineeId: number) => void
}

function todayIso(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function addWeeksIso(iso: string, weeks: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + weeks * 7)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function weeksBetweenIso(startIso: string, endIso: string): number {
  const start = new Date(`${startIso}T12:00:00`)
  const end = new Date(`${endIso}T12:00:00`)
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000)
  return Math.max(1, Math.min(52, Math.round(days / 7)))
}

export default function AssignToTraineeSheet({
  programId,
  programName,
  trainees,
  assignedTraineeIds,
  onClose,
  onAssigned,
}: Props) {
  const theme = useTheme()
  const { t, translate } = useTranslation()

  const available = trainees.filter(
    tr => tr.relationStatus === 'ACTIVE' && !assignedTraineeIds.includes(tr.id)
  )

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [startDate, setStartDate] = useState(todayIso)
  const [weeks, setWeeks] = useState(4)
  const [endDate, setEndDate] = useState(() => addWeeksIso(todayIso(), 4))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleStartDateChange(value: string) {
    setStartDate(value)
    setEndDate(addWeeksIso(value, weeks))
    setError(null)
  }

  function handleWeeksChange(nextWeeks: number) {
    const clamped = Math.max(1, Math.min(52, nextWeeks))
    setWeeks(clamped)
    setEndDate(addWeeksIso(startDate, clamped))
    setError(null)
  }

  function handleEndDateChange(value: string) {
    if (value < startDate) {
      setEndDate(value)
      setError(t.trainingPrograms.assignProgram.endBeforeStart)
      return
    }
    setError(null)
    const snapped = nearestPresetWeeks(weeksBetweenIso(startDate, value))
    setWeeks(snapped)
    setEndDate(addWeeksIso(startDate, snapped))
  }

  async function handleAssign() {
    if (!selectedId) return
    if (endDate < startDate) {
      setError(t.trainingPrograms.assignProgram.endBeforeStart)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const activation = await trainingProgramActivationApi.assignToTrainee(programId, {
        traineeId: selectedId,
        durationWeeks: weeks,
        startDate,
      })
      onAssigned(activation, selectedId)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.trainingPrograms.assignProgram.error.assignFailed)
    } finally {
      setLoading(false)
    }
  }

  function getTraineeName(tr: UserRelation) {
    const full = [tr.firstName, tr.lastName].filter(Boolean).join(' ')
    return full || tr.username || `#${tr.id}`
  }

  const sectionLabel: CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.text.muted,
    marginBottom: 12,
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: theme.bgColor }}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 shrink-0"
        style={{ borderBottom: `1px solid ${theme.cardBorder}` }}
      >
        <button
          type="button"
          onClick={onClose}
          className="p-2 -ml-2 rounded-xl"
          style={{ color: theme.text.primary }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <h2 className="text-lg font-semibold" style={{ color: theme.text.primary }}>
          {t.trainingPrograms.assignProgram.title}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <p className="text-sm mb-6" style={{ color: theme.text.muted }}>
          {programName}
        </p>

        {available.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: theme.text.muted }}>
            {t.trainingPrograms.assignProgram.noTrainees}
          </p>
        ) : (
          <>
            <p style={sectionLabel}>{t.trainingPrograms.assignProgram.selectTrainee}</p>
            <div className="flex flex-col gap-2 mb-8">
              {available.map(tr => {
                const isSelected = selectedId === tr.id
                return (
                  <button
                    key={tr.id}
                    type="button"
                    onClick={() => setSelectedId(isSelected ? null : tr.id)}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left w-full min-h-[52px]"
                    style={{
                      backgroundColor: isSelected ? `${theme.buttonColor}12` : theme.cardBg,
                      border: isSelected
                        ? `2px solid ${theme.buttonColor}`
                        : `1px solid ${theme.cardBorder}`,
                    }}
                  >
                    <div
                      className="w-5 h-5 rounded-full shrink-0 border-2 flex items-center justify-center"
                      style={{
                        borderColor: isSelected ? theme.buttonColor : theme.cardBorder,
                        backgroundColor: isSelected ? theme.buttonColor : 'transparent',
                      }}
                    >
                      {isSelected && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <Avatar
                      photoUrl={tr.photoUrl}
                      name={getTraineeName(tr)}
                      size="sm"
                      className="shrink-0"
                    />
                    <span className="font-semibold text-sm" style={{ color: theme.text.primary }}>
                      {getTraineeName(tr)}
                    </span>
                  </button>
                )
              })}
            </div>

            <p style={sectionLabel}>{t.trainingPrograms.assignProgram.scheduleLabel}</p>
            <div
              className="rounded-2xl overflow-hidden mb-4"
              style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
            >
              <div
                className="flex items-center justify-between px-4 py-3.5"
                style={{ borderBottom: `1px solid ${theme.cardBorder}` }}
              >
                <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                  {t.trainingPrograms.assignProgram.startDate}
                </p>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => handleStartDateChange(e.target.value)}
                  className="text-sm font-semibold bg-transparent border-none outline-none shrink-0"
                  style={{ color: theme.text.primary, colorScheme: theme.isDark ? 'dark' : 'light' }}
                />
              </div>
              <div className="flex items-center justify-between px-4 py-3.5">
                <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                  {t.trainingPrograms.assignProgram.endDate}
                </p>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={e => handleEndDateChange(e.target.value)}
                  className="text-sm font-semibold bg-transparent border-none outline-none shrink-0"
                  style={{ color: theme.text.primary, colorScheme: theme.isDark ? 'dark' : 'light' }}
                />
              </div>
            </div>

            <p style={{ ...sectionLabel, marginBottom: 8 }}>{t.trainingPrograms.assignProgram.quickDuration}</p>
            <div className="flex gap-2 mb-6">
              {WEEK_PRESETS.map(w => {
                const active = weeks === w
                return (
                  <button
                    key={w}
                    type="button"
                    onClick={() => handleWeeksChange(w)}
                    className="flex-1 h-11 rounded-xl text-sm font-semibold transition-colors"
                    style={active
                      ? { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
                      : {
                          backgroundColor: theme.cardBg,
                          border: `1px solid ${theme.cardBorder}`,
                          color: theme.text.primary,
                        }
                    }
                  >
                    {translate('trainingPrograms.assignProgram.weekChip', { count: w })}
                  </button>
                )
              })}
            </div>

            {error && (
              <div className="rounded-xl p-3 mb-4 bg-red-500/10 border border-red-500/25">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </>
        )}
      </div>

      {available.length > 0 && (
        <div className="px-4 pb-8 pt-3 shrink-0" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleAssign}
            disabled={loading || !selectedId || endDate < startDate}
          >
            {loading ? t.trainingPrograms.assignProgram.assigning : t.trainingPrograms.assignProgram.assignButton}
          </Button>
        </div>
      )}
    </div>
  )
}
